/**
 * Pure MuJoCo → BiomechFrame mapping. No wasm: frame rotations, contact sign, load.
 * MuJoCo xmat is row-major; its columns are the body axes in world.
 * Free-joint qfrc (3.15) is [force world (3), torque body-frame about the body origin (3)].
 * Ball-joint qfrc is the holding torque in the child body frame.
 * cfrc_int is [torque (3), force (3)] in the world-aligned c-frame.
 * mj_contactForce's 6-vector is the wrench on geom[1], in the contact frame
 * (normal, then two tangents, stored as consecutive world axes).
 */
import { jointLoad } from "./constants.ts";
import { add, cross, mul, sub } from "./vec.ts";
import type { JointId, Vec3 } from "./types.ts";

export const IDENTITY_XMAT: readonly number[] = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/** v_world = R * v_local. */
export function rotateXmat(v: Vec3, xmat: readonly number[]): Vec3 {
  return [
    xmat[0]! * v[0] + xmat[1]! * v[1] + xmat[2]! * v[2],
    xmat[3]! * v[0] + xmat[4]! * v[1] + xmat[5]! * v[2],
    xmat[6]! * v[0] + xmat[7]! * v[1] + xmat[8]! * v[2],
  ];
}

/** v_local = R^T * v_world. */
export function rotateXmatT(v: Vec3, xmat: readonly number[]): Vec3 {
  return [
    xmat[0]! * v[0] + xmat[3]! * v[1] + xmat[6]! * v[2],
    xmat[1]! * v[0] + xmat[4]! * v[1] + xmat[7]! * v[2],
    xmat[2]! * v[0] + xmat[5]! * v[1] + xmat[8]! * v[2],
  ];
}

export function transposeXmat(m: readonly number[]): number[] {
  return [m[0]!, m[3]!, m[6]!, m[1]!, m[4]!, m[7]!, m[2]!, m[5]!, m[8]!];
}

/** Row-major product A*B. */
export function mulXmat(a: readonly number[], b: readonly number[]): number[] {
  const out: number[] = new Array(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] =
        a[r * 3]! * b[c]! + a[r * 3 + 1]! * b[3 + c]! + a[r * 3 + 2]! * b[6 + c]!;
    }
  }
  return out;
}

/** Child orientation relative to the parent: R_parent^T * R_child. */
export function relativeXmat(parent: readonly number[], child: readonly number[]): number[] {
  return mulXmat(transposeXmat(parent), child);
}

export function normalizeQuat(q: readonly number[]): [number, number, number, number] {
  const n = Math.hypot(q[0] ?? 0, q[1] ?? 0, q[2] ?? 0, q[3] ?? 0);
  if (!(n > 1e-12)) return [1, 0, 0, 0];
  return [q[0]! / n, q[1]! / n, q[2]! / n, q[3]! / n];
}

/** MuJoCo mju_mat2Quat (row-major). */
export function matToQuat(m: readonly number[]): [number, number, number, number] {
  let q0: number;
  let q1: number;
  let q2: number;
  let q3: number;
  const trace = m[0]! + m[4]! + m[8]!;
  if (trace > 0) {
    q0 = 0.5 * Math.sqrt(1 + trace);
    q1 = (0.25 * (m[7]! - m[5]!)) / q0;
    q2 = (0.25 * (m[2]! - m[6]!)) / q0;
    q3 = (0.25 * (m[3]! - m[1]!)) / q0;
  } else if (m[0]! > m[4]! && m[0]! > m[8]!) {
    q1 = 0.5 * Math.sqrt(1 + m[0]! - m[4]! - m[8]!);
    q0 = (0.25 * (m[7]! - m[5]!)) / q1;
    q2 = (0.25 * (m[1]! + m[3]!)) / q1;
    q3 = (0.25 * (m[2]! + m[6]!)) / q1;
  } else if (m[4]! > m[8]!) {
    q2 = 0.5 * Math.sqrt(1 - m[0]! + m[4]! - m[8]!);
    q0 = (0.25 * (m[2]! - m[6]!)) / q2;
    q1 = (0.25 * (m[1]! + m[3]!)) / q2;
    q3 = (0.25 * (m[5]! + m[7]!)) / q2;
  } else {
    q3 = 0.5 * Math.sqrt(1 - m[0]! - m[4]! + m[8]!);
    q0 = (0.25 * (m[3]! - m[1]!)) / q3;
    q1 = (0.25 * (m[2]! + m[6]!)) / q3;
    q2 = (0.25 * (m[5]! + m[7]!)) / q3;
  }
  return normalizeQuat([q0, q1, q2, q3]);
}

/** Keep the quaternion on the same hemisphere as the previous sample (finite differences). */
export function alignQuat(
  q: readonly [number, number, number, number],
  prev: readonly number[] | undefined,
): [number, number, number, number] {
  if (!prev) return q[0] < 0 ? [-q[0], -q[1], -q[2], -q[3]] : [q[0], q[1], q[2], q[3]];
  const dot = q[0] * prev[0]! + q[1] * prev[1]! + q[2] * prev[2]! + q[3] * prev[3]!;
  return dot < 0 ? [-q[0], -q[1], -q[2], -q[3]] : [q[0], q[1], q[2], q[3]];
}

/** Ball-joint 3-vector of qfrc_inverse, rotated into world. */
export function ballMomentWorld(qfrcLocal: Vec3, xmat: readonly number[]): Vec3 {
  return rotateXmat(qfrcLocal, xmat);
}

/**
 * Translational cfrc_int of the child body, in world.
 * `cmat` is the c-frame orientation (identity: MuJoCo's c-frame axes are world-aligned).
 */
export function interactionForceWorld(cfrcInt: readonly number[], cmat: readonly number[] = IDENTITY_XMAT): Vec3 {
  const local: Vec3 = [cfrcInt[3] ?? 0, cfrcInt[4] ?? 0, cfrcInt[5] ?? 0];
  return rotateXmat(local, cmat);
}

/**
 * Free-joint residual. Force is already world; torque is body-frame about the body
 * origin and is shifted to the COM: m_com = R*m + (origin − com) × f.
 */
export function freejointResidual(
  qfrc: readonly number[],
  xmat: readonly number[],
  origin: Vec3,
  com: Vec3,
): { f: Vec3; m: Vec3 } {
  const f: Vec3 = [qfrc[0] ?? 0, qfrc[1] ?? 0, qfrc[2] ?? 0];
  const mOrigin = rotateXmat([qfrc[3] ?? 0, qfrc[4] ?? 0, qfrc[5] ?? 0], xmat);
  const m = add(mOrigin, cross(sub(origin, com), f));
  return { f, m };
}

/** Contact-frame vector → world. `frame9` stores normal, tangent, bitangent as consecutive axes. */
export function contactVectorWorld(local: Vec3, frame9: readonly number[]): Vec3 {
  const n: Vec3 = [frame9[0] ?? 0, frame9[1] ?? 0, frame9[2] ?? 0];
  const t: Vec3 = [frame9[3] ?? 0, frame9[4] ?? 0, frame9[5] ?? 0];
  const b: Vec3 = [frame9[6] ?? 0, frame9[7] ?? 0, frame9[8] ?? 0];
  return add(add(mul(n, local[0]), mul(t, local[1])), mul(b, local[2]));
}

/**
 * mj_contactForce wrench in world. The reported wrench acts on geom[1];
 * geom[0] is the reaction (negated).
 */
export function contactWrenchWorld(
  force6: readonly number[],
  frame9: readonly number[],
  geomIndex: 0 | 1,
): { f: Vec3; m: Vec3 } {
  const sign = geomIndex === 0 ? -1 : 1;
  const f = mul(
    contactVectorWorld([force6[0] ?? 0, force6[1] ?? 0, force6[2] ?? 0], frame9),
    sign,
  );
  const m = mul(
    contactVectorWorld([force6[3] ?? 0, force6[4] ?? 0, force6[5] ?? 0], frame9),
    sign,
  );
  return { f, m };
}

/** |m| / capacity, using the estimator's joint table. */
export function momentLoad(joint: JointId, moment: Vec3, massKg: number): number {
  const mag = Math.hypot(moment[0], moment[1], moment[2]);
  return jointLoad(joint, mag, massKg);
}
